import React from 'react';

/**
 * True when a lazy-loaded chunk failed to download, which after a deploy
 * means the page is running an old build.
 */
export const isChunkLoadError = (error: unknown) => {
  const err = error as { name?: string; message?: string } | null;
  return Boolean(
    err &&
      (err.name === 'ChunkLoadError' ||
        /Loading (CSS )?chunk [\w-]+ failed|Failed to fetch dynamically imported module|Importing a module script failed/i.test(
          err.message || '',
        )),
  );
};

/**
 * Error boundary with a render-prop fallback. Used three ways: around the
 * whole shell (full-page crash), around the route outlet (inline crash or
 * "updated"), and around sections (compact).
 *
 * @param fallback - Renders the error UI; call `reset` to try again
 * @param onError - Report the error (captureError)
 * @param resetKey - When this changes (e.g. the pathname), the boundary
 *   clears itself so navigating away recovers
 */
export class AllianceErrorBoundary extends React.Component<
  {
    children: React.ReactNode;
    fallback: (params: { error: Error; reset: () => void }) => React.ReactNode;
    onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
    resetKey?: unknown;
  },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps: { resetKey?: unknown }) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.reset();
    }
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      return this.props.fallback({
        error: this.state.error,
        reset: this.reset,
      });
    }
    return this.props.children;
  }
}
